import { NotificationStatus } from '../../../types';
import { postRequest } from '../../../utils';

const getErrorMessage = (errors: unknown, fallback: string): string => {
  if (Array.isArray(errors)) {
    const first = errors[0] as { message?: string } | undefined;
    if (first?.message) {
      return first.message;
    }
  }

  if (typeof errors === 'string' && errors) {
    return errors;
  }

  return fallback;
};

export type NotificationUpdateResponse = {
  success: boolean;
  data?: {
    userNotificationId: number;
    status: NotificationStatus;
  };
  error?: string;
};

/**
 * Defines allowed state transitions to prevent illegal status changes.
 */
const VALID_TRANSITIONS: Record<NotificationStatus, NotificationStatus[]> = {
  UNREAD: ['READ', 'ACCEPTED', 'REJECTED', 'DELETED'],
  READ: ['UNREAD', 'ACCEPTED', 'REJECTED', 'DELETED'],
  ACCEPTED: [],
  REJECTED: [],
  DELETED: [], // Terminal state
};

/**
 * Checks if a status transition is logically permitted.
 * @param current - The current status of the notification.
 * @param next - The desired new status.
 * @returns boolean
 */
export const canUpdateStatus = (current: NotificationStatus, next: NotificationStatus): boolean => {
  return VALID_TRANSITIONS[current]?.includes(next) ?? false;
};

/**
 * Updates a specific user notification status via the Express API.
 * Handles validation and internal error catching so the caller doesn't have to.
 * @param userNotificationId - The unique mapping ID for the user's notification.
 * @param currentStatus - Current status from the UI state.
 * @param newStatus - The target status (e.g., 'READ', 'ACCEPTED').
 * @returns {Promise<NotificationUpdateResponse>} A result object containing success status and data or error.
 */
export const updateNotificationStatus = async (
  userNotificationId: number,
  currentStatus: NotificationStatus,
  newStatus: NotificationStatus,
): Promise<NotificationUpdateResponse> => {
  // Business Logic Validation
  if (!canUpdateStatus(currentStatus, newStatus)) {
    return {
      success: false,
      error: `Invalid transition: Cannot move from ${currentStatus} to ${newStatus}`,
    };
  }

  try {
    const result = await postRequest('/api/notification/updateNotification', {
      userNotificationId,
      status: newStatus,
    });

    if (result?.errors) {
      return {
        success: false,
        error: getErrorMessage(result.errors, 'Failed to update notification status'),
      };
    }

    // Success Return
    return {
      success: true,
      data: {
        userNotificationId: result.updated.userNotificationId,
        status: result.updated.status,
      },
    };
  } catch (err) {
    console.error('Notification Update Error:', err);
    return {
      success: false,
      error: 'A network error occurred while updating the notification',
    };
  }
};